import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import {
  AdminEnvironment,
  buildEnvironmentApiUrl,
  getAdminEnvironment,
  getAdminEnvironmentConfig,
  getEnvironmentTokenKey,
} from "./adminEnvironment";
import { API_BASE_URL } from "../contants";

const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _adminAuthRetried?: boolean;
};

type RefreshFailureReason = "expired" | "network";

class RefreshFailure extends Error {
  constructor(readonly reason: RefreshFailureReason) {
    super(reason);
  }
}

const refreshPromises: Partial<Record<AdminEnvironment, Promise<string>>> = {};
const installedClients = new WeakSet<AxiosInstance>();

export const saveAdminTokens = (
  accessToken: string,
  refreshToken: string,
  environment = getAdminEnvironment(),
) => {
  localStorage.setItem(getEnvironmentTokenKey(environment, "access"), accessToken);
  localStorage.setItem(getEnvironmentTokenKey(environment, "refresh"), refreshToken);
};

export const clearAdminTokens = (environment?: AdminEnvironment) => {
  const environments = environment ? [environment] : (["prod", "dev"] as AdminEnvironment[]);
  environments.forEach((key) => {
    localStorage.removeItem(getEnvironmentTokenKey(key, "access"));
    localStorage.removeItem(getEnvironmentTokenKey(key, "refresh"));
  });
};

export const getAdminAccessToken = (environment = getAdminEnvironment()) =>
  localStorage.getItem(getEnvironmentTokenKey(environment, "access"));

const getAdminRefreshToken = (environment: AdminEnvironment) =>
  localStorage.getItem(getEnvironmentTokenKey(environment, "refresh"));

const getJwtExpiryMs = (token: string) => {
  try {
    const encodedPayload = token.split(".")[1];
    if (!encodedPayload) return null;
    const normalizedPayload = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(normalizedPayload));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const shouldRefreshAccessToken = (accessToken: string) => {
  const expiryMs = getJwtExpiryMs(accessToken);
  return expiryMs !== null && expiryMs - Date.now() <= REFRESH_THRESHOLD_MS;
};

const refreshAccessToken = async (environment: AdminEnvironment): Promise<string> => {
  const refreshToken = getAdminRefreshToken(environment);
  if (!refreshToken) {
    clearAdminTokens(environment);
    throw new RefreshFailure("expired");
  }

  try {
    const response = await axios.post(
      buildEnvironmentApiUrl(environment, "/online/auth/refresh"),
      undefined,
      { headers: { "refresh-token": refreshToken } },
    );
    const accessToken = response.data?.access_token;
    const nextRefreshToken = response.data?.refresh_token;

    if (!accessToken || !nextRefreshToken) {
      clearAdminTokens();
      throw new RefreshFailure("expired");
    }

    saveAdminTokens(accessToken, nextRefreshToken, environment);
    return accessToken;
  } catch (error) {
    if (error instanceof RefreshFailure) throw error;
    const status = (error as AxiosError)?.response?.status;
    if (status === 400 || status === 401 || status === 403) {
      clearAdminTokens(environment);
      throw new RefreshFailure("expired");
    }
    throw new RefreshFailure("network");
  }
};

export const refreshAdminAccessToken = (environment = getAdminEnvironment()) => {
  if (!refreshPromises[environment]) {
    refreshPromises[environment] = refreshAccessToken(environment).finally(() => {
      delete refreshPromises[environment];
    });
  }
  return refreshPromises[environment]!;
};

export const getValidAdminAccessToken = async (environment = getAdminEnvironment()) => {
  const accessToken = getAdminAccessToken(environment);
  if (!accessToken) return null;
  if (!shouldRefreshAccessToken(accessToken)) return accessToken;

  try {
    return await refreshAdminAccessToken(environment);
  } catch (error) {
    // 네트워크 단절은 관리자 세션을 즉시 폐기하지 않는다. 아직 유효할 수 있는
    // access token으로 현재 화면을 유지하고, 다음 인증 요청에서 다시 시도한다.
    if (error instanceof RefreshFailure && error.reason === "network") return accessToken;
    throw error;
  }
};

const isRefreshOrLoginRequest = (url: string) =>
  url.includes("/online/auth/refresh") || url.includes("/online/auth/signin");

const isAdminApiRequest = (config: InternalAxiosRequestConfig) => {
  const url = config.url || "";
  const baseUrl = config.baseURL || "";
  if (isRefreshOrLoginRequest(url)) return false;

  return url.startsWith("/admin/")
    || url.startsWith("/online/")
    || url.startsWith(`${API_BASE_URL}/`)
    || url.includes("/api/admin/")
    || url.includes("/api/online/")
    || baseUrl.includes("/api");
};

const hasAccessTokenHeader = (config: InternalAxiosRequestConfig) =>
  Boolean(AxiosHeaders.from(config.headers).get("access-token"));

const redirectToLogin = () => {
  clearAdminTokens();
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

const retryWithToken = (client: AxiosInstance, config: RetriableRequestConfig, accessToken: string) => {
  const headers = AxiosHeaders.from(config.headers);
  headers.set("access-token", accessToken);
  config.headers = headers;
  config._adminAuthRetried = true;
  return client.request(config);
};

export const installAdminAuthInterceptor = (client: AxiosInstance) => {
  if (installedClients.has(client)) return;
  installedClients.add(client);

  client.interceptors.request.use((config) => {
    const url = config.url || "";
    if (isRefreshOrLoginRequest(url)) return config;
    if (!isAdminApiRequest(config)) return config;

    const environment = getAdminEnvironment();
    const selectedBaseUrl = getAdminEnvironmentConfig(environment).apiBaseUrl;

    if (/^https?:\/\//i.test(url)) {
      try {
        const requestUrl = new URL(url);
        const selectedBase = new URL(selectedBaseUrl);
        const basePath = selectedBase.pathname.replace(/\/$/, "");
        config.url = requestUrl.pathname.startsWith(basePath)
          ? `${requestUrl.pathname.slice(basePath.length) || "/"}${requestUrl.search}`
          : `${requestUrl.pathname}${requestUrl.search}`;
      } catch {
        config.url = url;
      }
    }

    config.baseURL = selectedBaseUrl;
    const headers = AxiosHeaders.from(config.headers);
    const accessToken = getAdminAccessToken(environment);
    if (accessToken) headers.set("access-token", accessToken);
    config.headers = headers;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetriableRequestConfig | undefined;
      if (error.response?.status !== 401 || !config || !isAdminApiRequest(config) || !hasAccessTokenHeader(config)) {
        return Promise.reject(error);
      }

      if (config._adminAuthRetried) {
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        return await retryWithToken(client, config, await refreshAdminAccessToken());
      } catch (refreshError) {
        if (refreshError instanceof RefreshFailure && refreshError.reason === "expired") {
          redirectToLogin();
        }
        return Promise.reject(error);
      }
    },
  );
};
